import clsx from 'clsx';
import { cloneElement, forwardRef, isValidElement } from 'react';
import Button from '../Button';
import TextField from '../TextField';
import { getQTipData } from '../Tooltip/qTip.utilities';
import type { InputGroupProps } from './InputGroup.types';

const baseClassNames = `
  tw:relative
  tw:flex
  tw:w-full
  tw:flex-wrap
  tw:rounded-md
  tw:outline-none
`;

const errorClassNames = 'tw:border-sq-danger';

const borderColorClassNames = `
  tw:border-sq-disabled-gray
  tw:focus:border-sq-theme-dark
  tw:active:border-sq-theme-dark
  tw:dark:border-sq-dark-disabled-gray
  tw:dark:focus:border-sq-theme-darker
  tw:dark:active:border-sq-theme-darker
`;

const fieldClassNames = `
  tw:border
  tw:border-solid
  tw:p-1
  tw:px-3
  tw:py-1
  tw:leading-normal
  tw:outline-none
`;

const darkThemeClassNames = `
  tw:dark:bg-sq-dark-background
  tw:dark:text-sq-dark-text
  tw:disabled:dark:text-sq-disabled-gray
`;
const lightThemeClassNames = `
  tw:text-sq-text-color
  tw:disabled:text-sq-darkish-gray
`;

/**
 * InputGroup.
 */
export const InputGroup = forwardRef<HTMLInputElement, InputGroupProps>((props, ref) => {
  const {
    readonly = false,
    onChange,
    onKeyUp,
    onFocus,
    onBlur,
    onKeyDown,
    name,
    value,
    placeholder,
    append = [],
    extraClassNames = '',
    id,
    testId,
    showError,
    required,
    autoComplete,
    autoFocus,
    disabled,
    type,
    step,
    min,
    max,
    field,
    maxLength,
    minLength,
    inputGroup,
    ...tooltipProps
  } = props;
  const tooltipData = getQTipData(tooltipProps);

  const appliedClassNames = clsx(baseClassNames, extraClassNames);

  const elementsToAppend = append.filter(Boolean);
  const effectiveInputGroup = inputGroup ?? (elementsToAppend.length > 0 ? 'left' : undefined);

  const fieldBorderRadius =
    effectiveInputGroup === 'left'
      ? 'tw:rounded-l-md tw:rounded-r-none'
      : effectiveInputGroup === 'right'
        ? 'tw:rounded-r-md tw:rounded-l-none'
        : 'tw:rounded-md';

  const fieldAppliedClassNames = clsx(
    fieldClassNames,
    extraClassNames,
    lightThemeClassNames,
    darkThemeClassNames,
    fieldBorderRadius,
    showError ? errorClassNames : borderColorClassNames,
  );

  // Only pass inputGroup to React components (not DOM elements like div, span, etc.)
  const isReactComponent = isValidElement(field) && typeof field.type !== 'string';
  const fieldWithInputGroup = isReactComponent
    ? cloneElement(field as React.ReactElement<{ inputGroup?: 'left' | 'right' }>, {
        inputGroup: effectiveInputGroup,
      })
    : field;

  return (
    <div id={`input-group-${id}`} className={appliedClassNames}>
      {field ? (
        <div
          data-testid={testId}
          className={clsx('tw:flex tw:flex-1 tw:cursor-pointer tw:active:z-50', fieldAppliedClassNames)}>
          {fieldWithInputGroup}
        </div>
      ) : (
        <TextField
          testId={testId}
          readonly={readonly}
          onChange={onChange}
          onKeyUp={onKeyUp}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          name={name}
          ref={ref}
          showError={showError}
          value={value}
          required={required}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          id={id}
          type={type}
          disabled={disabled}
          step={step}
          min={min}
          max={max}
          maxLength={maxLength}
          minLength={minLength}
          inputGroup={effectiveInputGroup}
          extraClassNames={clsx('tw:flex tw:w-full tw:flex-1 tw:focus:z-30', extraClassNames)}
          {...tooltipData}
        />
      )}

      {elementsToAppend.map((item, index) => {
        return item?.variant === 'button' ? (
          <Button
            key={index}
            extraClassNames={clsx(
              index ? 'tw:-ml-px' : 'tw:ml-0',
              'tw:rounded-none tw:last:rounded-r-md tw:focus:z-40 tw:focus:border',
            )}
            {...item.buttonProps}
          />
        ) : (
          <div
            key={index}
            className={clsx(
              borderColorClassNames,
              index ? 'tw:-ml-px' : 'tw:ml-0',
              'tw:flex tw:items-center tw:justify-center tw:rounded-none tw:border tw:last:rounded-r-md tw:active:z-30 tw:active:border',
            )}>
            {item?.element}
          </div>
        );
      })}
    </div>
  );
});
InputGroup.displayName = 'InputGroup';
